import React from "react";
import { Box } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import GuidanceDialogShell from "../GuidanceDialog/GuidanceDialogShell";

export interface FormModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  /** Optional actions in header (e.g. Print button), shown before close button */
  headerActions?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Horizontal alignment of footer actions (default: center) */
  footerAlign?: "start" | "center" | "end";
  /** Max width in px or breakpoint key */
  maxWidth?: number | "xs" | "sm" | "md" | "lg" | "xl";
  /** Additional sx for Dialog paper (e.g. print styles) */
  BoxSx?: object;
  /** Optional data-testid for E2E (e.g. add-product-modal) */
  dataTestId?: string;
}

/** Shared field styling for form inputs inside FormModal */
export const formFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    backgroundColor: "grey.50",
    "&:hover": { backgroundColor: "grey.100" },
    "&.Mui-focused": { backgroundColor: "background.paper" }
  }
};

/** Shared section title styling */
export const formSectionTitleSx = {
  fontSize: "0.75rem",
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
  color: "text.secondary",
  mb: 1.5,
  display: "block"
};

/** Shared scrollbar styles for modal content - matches app.css */
export { formModalScrollbarSx } from "../GuidanceDialog/guidanceDialogStyles";

const FormModal: React.FC<FormModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  headerActions,
  children,
  footer,
  footerAlign = "center",
  maxWidth = "md",
  BoxSx = {},
  dataTestId
}) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  const resolvedMaxWidth =
    typeof maxWidth === "number"
      ? maxWidth
      : maxWidth && maxWidth in theme.breakpoints.values
        ? theme.breakpoints.values[
            maxWidth as keyof typeof theme.breakpoints.values
          ]
        : theme.breakpoints.values.md;

  return (
    <GuidanceDialogShell
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      icon={icon}
      headerActions={headerActions}
      footer={footer}
      footerAlign={footerAlign}
      maxWidth={resolvedMaxWidth}
      dataTestId={dataTestId}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: matchMD ? 1.5 : 2,
          width: { xs: "100%", md: "80%" },
          maxWidth: "100%",
          mx: { xs: 0, md: "auto" },
          ...BoxSx
        }}
      >
        {children}
      </Box>
    </GuidanceDialogShell>
  );
};

export default FormModal;
